import { useEffect, useRef, useState } from "react";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateOnly } from "../dates";
import { Link } from "react-router";
import readXlsxFile from "read-excel-file/browser";
import {
  Permissions as P,
  buildingHandoverCatalogue,
  blankBuildingHandoverRow,
  buildingHandoverOutstanding,
  handoverApplicability,
  handoverSupplyStatuses,
  handoverReviews,
  type BuildingHandoverPack as Pack,
  type BuildingHandoverRow as Row,
} from "@myfm/domain";
import { api, authenticatedFetch } from "../api";
import { useCapability } from "../ui/StaffSession";
import { Form } from "../ui/Form";
import { ErrorState, Loading, reviewedApplicationError } from "../ui/State";
import { withBuildingScope } from "../ui/building-scope";
import { parseHandoverWorkbook } from "./handover-workbook";
import "./building-handover-pack.css";

export function BuildingHandoverPack({
  building,
  initialDirection = "Incoming",
}: {
  building: string;
  initialDirection?: Pack["direction"];
}) {
  const [direction, setDirection] = useState<Pack["direction"]>(initialDirection),
    [dirty, setDirty] = useState(false);
  return (
    <section className="panel transfer-pack" aria-labelledby="transfer-pack-title">
      <h2 id="transfer-pack-title">Building handover pack</h2>
      <p>
        Use the standard checklist for every building and the additional HRB schedule where
        applicable. The agent can return the workbook without a BG OS account.
      </p>
      <div className="actions">
        <a href="/templates/building-handover.xlsx" download>
          Download branded handover template
        </a>
      </div>
      <label>
        Handover direction
        <select
          disabled={dirty}
          value={direction}
          onChange={(e) => setDirection(e.target.value as Pack["direction"])}
        >
          <option>Incoming</option>
          <option>Outgoing</option>
        </select>
      </label>
      <PackEditor
        key={`${building}:${direction}`}
        building={building}
        direction={direction}
        onDirty={setDirty}
      />
    </section>
  );
}
function PackEditor({
  building,
  direction,
  onDirty,
}: {
  building: string;
  direction: Pack["direction"];
  onDirty: (value: boolean) => void;
}) {
  const canManage = useCapability(P.ManageClientOnboarding),
    [data, setData] = useState<Pack>(),
    [draft, setDraft] = useState<Pack>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    [notice, setNotice] = useState(""),
    [refresh, setRefresh] = useState(0),
    [filter, setFilter] = useState("Standard"),
    [page, setPage] = useState(0),
    [editing, setEditing] = useState<string>(),
    [preview, setPreview] = useState<Record<string, Row>>(),
    [confirmed, setConfirmed] = useState(false);
  const pending = useRef<{ body: string; version: number } | undefined>(undefined),
    locked = useRef(false);
  const url = withBuildingScope(
    `/api/building-handover-pack/${building}?direction=${direction}`,
    building,
  );
  const dirty = Boolean(data && draft && JSON.stringify(data) !== JSON.stringify(draft));
  useEffect(() => onDirty(dirty), [dirty, onDirty]);
  useEffect(() => {
    const abort = new AbortController();
    void api<Pack>(url, { signal: abort.signal })
      .then((value) => {
        if (
          value.building !== building ||
          value.direction !== direction ||
          !Number.isSafeInteger(value.version)
        )
          throw reviewedApplicationError("Handover response does not match this building.");
        if (!abort.signal.aborted) {
          setData(value);
          setDraft(value);
          setError(undefined);
        }
      })
      .catch((cause) => {
        if (!abort.signal.aborted) setError(cause);
      });
    return () => abort.abort();
  }, [url, building, direction, refresh]);
  if (!data || !draft)
    return error ? <ErrorState error={error} /> : <Loading heading="Loading handover pack" />;
  const updateRow = (id: string, patch: Partial<Row>) =>
    setDraft({
      ...draft,
      rows: {
        ...draft.rows,
        [id]: {
          ...(draft.rows[id] ?? blankBuildingHandoverRow()),
          ...patch,
          review: patch.review ?? "Not reviewed",
        },
      },
    });
  const all = buildingHandoverCatalogue.filter((item) =>
    filter === "Outstanding"
      ? buildingHandoverOutstanding(draft.rows[item.id] ?? blankBuildingHandoverRow())
      : item.pack === filter,
  );
  const shown = all.slice(page * 8, page * 8 + 8);
  const save = async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError(undefined);
    setNotice("");
    try {
      const payload = pending.current ?? {
        version: data.version,
        body: JSON.stringify({
          direction,
          expectedVersion: data.version,
          hrbStatus: draft.hrbStatus,
          transferDate: draft.transferDate,
          agentContact: draft.agentContact,
          rows: draft.rows,
          reason: `Record ${direction.toLowerCase()} building handover information`,
          idempotencyKey: crypto.randomUUID(),
        }),
      };
      pending.current = payload;
      const result = await api<{ building: string; direction: string; version: number }>(url, {
        method: "POST",
        body: payload.body,
      });
      if (
        result.building !== building ||
        result.direction !== direction ||
        result.version !== payload.version + 1
      )
        throw reviewedApplicationError(
          "The saved handover could not be confirmed. Retry the same save.",
        );
      const saved = { ...draft, version: result.version };
      setData(saved);
      setDraft(saved);
      pending.current = undefined;
      setNotice("Handover saved. Building records and management readiness remain separate.");
      return true;
    } catch (cause) {
      setError(cause);
      if ([400, 403, 404, 409, 422].includes((cause as { statusCode?: number }).statusCode ?? 0))
        pending.current = undefined;
      return false;
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  const csv = async () => {
    try {
      const response = await authenticatedFetch(url + "&format=csv");
      if (!response.ok) throw Error("The handover export could not be downloaded.");
      const link = document.createElement("a"),
        object = URL.createObjectURL(await response.blob());
      link.href = object;
      link.download = `building-handover-${direction.toLowerCase()}.csv`;
      link.click();
      URL.revokeObjectURL(object);
    } catch (cause) {
      setError(cause);
    }
  };
  const reloadKeepingEntries = async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    try {
      const latest = await api<Pack>(url);
      if (latest.building !== building || latest.direction !== direction)
        throw reviewedApplicationError("Handover response does not match this building.");
      const changed = Object.fromEntries(
        Object.entries(draft.rows).filter(
          ([id, row]) => JSON.stringify(row) !== JSON.stringify(data.rows[id]),
        ),
      );
      setData(latest);
      setDraft({
        ...latest,
        hrbStatus: draft.hrbStatus !== data.hrbStatus ? draft.hrbStatus : latest.hrbStatus,
        transferDate:
          draft.transferDate !== data.transferDate ? draft.transferDate : latest.transferDate,
        agentContact:
          draft.agentContact !== data.agentContact ? draft.agentContact : latest.agentContact,
        rows: { ...latest.rows, ...changed },
      });
      setNotice(
        "Latest version loaded. Your changed entries are retained over it. Review any overlapping changes before saving.",
      );
      setError(undefined);
    } catch (cause) {
      setError(cause);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  return (
    <>
      <p>
        {direction} handover · saved version {data.version}. Handover HRB scope: {draft.hrbStatus}.
      </p>
      {Boolean(error) && <ErrorState error={error} />} {notice && <p role="status">{notice}</p>}
      <div className="actions">
        <button
          type="button"
          className="secondary"
          onClick={() => void csv()}
          disabled={busy || dirty}
        >
          Export saved checklist CSV
        </button>
        {!dirty ? (
          <button
            type="button"
            className="secondary"
            onClick={() => setRefresh((n) => n + 1)}
            disabled={busy}
          >
            Reload saved handover
          </button>
        ) : (
          <button
            type="button"
            className="secondary"
            onClick={() => void reloadKeepingEntries()}
            disabled={busy || Boolean(pending.current)}
          >
            Reload latest version and keep my entries
          </button>
        )}
      </div>
      <Form
        aria-label={`${direction} handover pack`}
        dirty={dirty}
        onSubmit={(event) => {
          event.preventDefault();
          return save();
        }}
      >
        <fieldset disabled={!canManage || busy || Boolean(pending.current)}>
          <legend>Transfer details</legend>
          <div className="transfer-pack-fields">
            <label>
              Handover HRB scope
              <select
                value={draft.hrbStatus}
                onChange={(e) =>
                  setDraft({ ...draft, hrbStatus: e.target.value as Pack["hrbStatus"] })
                }
              >
                <option>To confirm</option>
                <option>HRB</option>
                <option>Not HRB</option>
              </select>
            </label>
            <label>
              Management transfer date
              <DateOnlyInput
                value={draft.transferDate}
                onChange={(value) => setDraft({ ...draft, transferDate: value })}
              />
            </label>
            <label>
              Agent and lead contact
              <input
                maxLength={500}
                value={draft.agentContact}
                onChange={(e) => setDraft({ ...draft, agentContact: e.target.value })}
              />
            </label>
          </div>
        </fieldset>
        <p>
          This transfer scope starts from the building register and preserves all entries. It does
          not change the building’s regulatory classification. An agent change does not itself
          transfer AP/PAP responsibilities. Do not enter passwords, access codes or individual
          health information.
        </p>
        <div className="actions">
          {["Standard", "HRB", "Outstanding"].map((value) => (
            <button
              type="button"
              className="secondary"
              key={value}
              aria-pressed={filter === value}
              onClick={() => {
                setFilter(value);
                setPage(0);
              }}
            >
              {value === "HRB"
                ? "Additional HRB"
                : value === "Outstanding"
                  ? "Outstanding information"
                  : "Standard checklist"}
            </button>
          ))}
        </div>
        <p>
          {all.length} items in this view. Supplied information still needs review. References
          identify existing originals and exports.
        </p>
        <div className="transfer-pack-items">
          {shown.map((item) => {
            const row = draft.rows[item.id] ?? blankBuildingHandoverRow();
            return (
              <details
                key={item.id}
                open={editing === item.id}
                onToggle={(e) => {
                  if (e.currentTarget.open) setEditing(item.id);
                  else if (editing === item.id) setEditing(undefined);
                }}
              >
                <summary>
                  {item.id} · {item.title} · {row.applies === "No" ? "Not applicable" : row.status}{" "}
                  · {row.review}
                </summary>
                <p>{item.request}</p>
                <p>
                  Suggested stage: {item.stage}.{" "}
                  {row.holder && `Holder / follow-up owner: ${row.holder}. `}
                  {row.dueDate && `Due: ${formatDateOnly(row.dueDate)}.`}
                </p>
                <fieldset disabled={!canManage || busy || Boolean(pending.current)}>
                  <legend>{item.id} transfer information</legend>
                  <div className="transfer-pack-fields">
                    <label>
                      Applies?
                      <select
                        value={row.applies}
                        onChange={(e) =>
                          updateRow(item.id, { applies: e.target.value as Row["applies"] })
                        }
                      >
                        {handoverApplicability.map((v) => (
                          <option key={v}>{v}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Supply status
                      <select
                        value={row.status}
                        onChange={(e) =>
                          updateRow(item.id, { status: e.target.value as Row["status"] })
                        }
                      >
                        {handoverSupplyStatuses.map((v) => (
                          <option key={v}>{v}</option>
                        ))}
                      </select>
                    </label>
                    <label>
                      File / folder reference
                      <input
                        maxLength={5000}
                        value={row.reference}
                        onChange={(e) => updateRow(item.id, { reference: e.target.value })}
                      />
                    </label>
                    <label>
                      Record date / cut-off
                      <DateOnlyInput
                        value={row.recordDate}
                        onChange={(value) => updateRow(item.id, { recordDate: value })}
                      />
                    </label>
                    <label>
                      Holder / follow-up owner
                      <input
                        maxLength={240}
                        value={row.holder}
                        onChange={(e) => updateRow(item.id, { holder: e.target.value })}
                      />
                    </label>
                    <label>
                      Agreed due date
                      <DateOnlyInput
                        value={row.dueDate}
                        onChange={(value) => updateRow(item.id, { dueDate: value })}
                      />
                    </label>
                    <label>
                      Next action
                      <textarea
                        maxLength={5000}
                        value={row.nextAction}
                        onChange={(e) => updateRow(item.id, { nextAction: e.target.value })}
                      />
                    </label>
                    <label>
                      Notes / limitations
                      <textarea
                        required={row.applies === "No"}
                        maxLength={5000}
                        value={row.notes}
                        onChange={(e) => updateRow(item.id, { notes: e.target.value })}
                      />
                    </label>
                    <label>
                      Incoming review
                      <select
                        value={row.review}
                        onChange={(e) =>
                          updateRow(item.id, { review: e.target.value as Row["review"] })
                        }
                      >
                        {handoverReviews.map((v) => (
                          <option key={v}>{v}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                </fieldset>
              </details>
            );
          })}
        </div>
        <div className="actions">
          <button
            type="button"
            className="secondary"
            disabled={page === 0}
            onClick={() => setPage((n) => n - 1)}
          >
            Previous items
          </button>
          <span>
            Page {page + 1} of {Math.max(1, Math.ceil(all.length / 8))}
          </span>
          <button
            type="button"
            className="secondary"
            disabled={(page + 1) * 8 >= all.length}
            onClick={() => setPage((n) => n + 1)}
          >
            Next items
          </button>
        </div>
        {canManage && (
          <button disabled={busy || (!dirty && !pending.current)}>
            {busy ? "Saving…" : pending.current ? "Retry same handover save" : "Save handover pack"}
          </button>
        )}
      </Form>
      {canManage && (
        <details>
          <summary>Import returned checklist workbook</summary>
          <p>
            Choose the completed template for this building. Preview the changed items, then save.
            External acceptance marks are reset for our review. Outstanding-information rows linked
            by item ID are retained in the checklist’s follow-up notes. Operational Actions remain
            in My Work.
          </p>
          <input
            aria-label="Returned handover workbook"
            type="file"
            accept=".xlsx"
            disabled={busy || dirty || Boolean(pending.current)}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              setPreview(undefined);
              setConfirmed(false);
              if (!file) return;
              if (file.size > 5 * 1024 * 1024) {
                setError(Error("Choose a workbook under 5 MB."));
                return;
              }
              try {
                const sheets: Record<string, unknown[][]> = {};
                for (const sheet of await readXlsxFile(file)) sheets[sheet.sheet] = sheet.data;
                setPreview(parseHandoverWorkbook(sheets));
                setError(undefined);
              } catch (cause) {
                setError(cause);
              }
            }}
          />
          {preview && (
            <>
              <p>
                {Object.keys(preview).length} completed entries. Existing matching entries will be
                replaced. Other entries are retained.
              </p>
              <ul>
                {Object.keys(preview).map((id) => (
                  <li key={id}>
                    {id} · {buildingHandoverCatalogue.find((item) => item.id === id)?.title}
                  </li>
                ))}
              </ul>
              <label>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                I have checked that this workbook belongs to the selected building and reviewed
                these replacements.
              </label>
              <button
                type="button"
                disabled={!confirmed || busy || dirty}
                onClick={() => {
                  setDraft({ ...draft, rows: { ...draft.rows, ...preview } });
                  setPreview(undefined);
                  setNotice("Workbook entries staged. Review them and save the handover pack.");
                }}
              >
                Stage workbook entries
              </button>
            </>
          )}
        </details>
      )}
      <p>
        <Link to={withBuildingScope(`/buildings/${building}/onboarding`, building)}>
          Open building onboarding and records
        </Link>
      </p>
    </>
  );
}
